/**
 * @fileoverview NotificationBell Component
 * @description Bell icon with badge and dropdown panel for notifications.
 *              Displays unread count, notification list, and mark-as-read actions.
 * @module components/common/NotificationBell
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Calendar, X, Download, Trash2 } from 'lucide-react';
import { useNotifications } from '@/hooks';
import { incidentApi } from '@/features/IncidentAnalysis/services/incidentApi';
import './NotificationBell.css';

const CATEGORY_ICONS = {
  EVENT_UPDATED: '✏️',
  STATUS_CHANGED: '🔄',
  EVENT_EXTENDED: '⏱️',
  EVENT_CLOSED: '✅',
  EVENT_DELETED: '🗑️',
  PARTICIPANT_ADDED: '👤',
  LOC_REPORT: '📊',
  REPORT_READY: '📥',
};

const CATEGORY_COLORS = {
  EVENT_UPDATED: '#0097A9',
  STATUS_CHANGED: '#01426A',
  EVENT_EXTENDED: '#F5A623',
  EVENT_CLOSED: '#00AF66',
  EVENT_DELETED: '#E31837',
  PARTICIPANT_ADDED: '#9013FE',
  LOC_REPORT: '#6366f1',
  REPORT_READY: '#7C3AED',
};

/**
 * Format a relative time string from an ISO date
 */
const getRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);
  const bellRef = useRef(null);
  const navigate = useNavigate();

  const {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearPastReports,
  } = useNotifications();

  const hasReportNotifications = notifications.some((n) => n._source === 'report');

  // Fetch notifications when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications({ includeRead: true, pageSize: 15 });
    }
  }, [isOpen, fetchNotifications]);

  // Close panel on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    setIsOpen(false);

    // Report-ready: trigger CSV download inline
    if (notification._source === 'report') {
      await downloadReportCsv(notification);
      return;
    }

    // Route LOC notifications to the LOC page
    if (notification._source === 'loc' || notification.category === 'LOC_REPORT') {
      navigate('/lines-of-code');
    } else {
      navigate('/test-calendar');
    }
  };

  /** Regenerate and download the CSV from the stored rows */
  const downloadReportCsv = async (notification) => {
    let rows = notification._csvRows;
    if (!rows || rows.length === 0) {
      try {
        const data = await incidentApi.reportResults(notification._runId ? { run_id: notification._runId } : {});
        rows = data?.results || [];
      } catch {
        rows = [];
      }
    }
    if (!rows || rows.length === 0) return;

    const HEADERS = [
      'Incident ID', 'Description', 'Close Description', 'Assigned Team',
      'Severity', 'Semantic Severity', 'Agentic Severity', 'Sev Match',
      'Semantic Priority', 'Agentic Priority', 'Priority Match',
      'Overall', 'Reason',
    ];
    const escape = (val) => {
      const s = String(val ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };
    const priStr = (v) => (v !== null && v !== undefined ? Number(v).toFixed(1) : '—');
    const lines = [
      HEADERS.join(','),
      ...rows.map((r) =>
        [
          r.incident_id, r.description, r.close_description, r.assigned_team,
          r.severity, r.semantic_severity, r.agentic_severity,
          r.sev_match === true ? 'Yes' : r.sev_match === false ? 'No' : '—',
          priStr(r.semantic_priority), priStr(r.agentic_priority),
          r.priority_match === true ? 'Yes' : r.priority_match === false ? 'No' : '—',
          priStr(r.overall), r.reason,
        ].map(escape).join(',')
      ),
    ];
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${(notification._label || 'report').replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  return (
    <div className="notification-bell-wrapper">
      <button
        ref={bellRef}
        className={`notification-bell-btn ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge-count">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div ref={panelRef} className="notification-panel">
          {/* Header */}
          <div className="notification-panel-header">
            <div className="notification-panel-title">
              <Bell size={18} />
              <span>Notifications</span>
              {unreadCount > 0 && (
                <span className="notification-unread-badge">{unreadCount}</span>
              )}
            </div>
            <div className="notification-panel-actions">
              {unreadCount > 0 && (
                <button
                  className="notification-action-btn"
                  onClick={handleMarkAllRead}
                  title="Mark all as read"
                >
                  <CheckCheck size={16} />
                </button>
              )}
              {hasReportNotifications && (
                <button
                  className="notification-action-btn"
                  onClick={clearPastReports}
                  title="Clear past report notifications"
                >
                  <Trash2 size={16} />
                </button>
              )}
              <button
                className="notification-action-btn"
                onClick={() => setIsOpen(false)}
                title="Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="notification-panel-content">
            {loading && notifications.length === 0 && (
              <div className="notification-empty">
                <div className="notification-spinner" />
                <span>Loading notifications...</span>
              </div>
            )}

            {!loading && notifications.length === 0 && (
              <div className="notification-empty">
                <Bell size={32} strokeWidth={1.5} />
                <span>No notifications yet</span>
                <p>Subscribe to events to get notified about changes.</p>
              </div>
            )}

            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => handleNotificationClick(notification)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNotificationClick(notification); } }}
              >
                <div
                  className="notification-item-indicator"
                  style={{ backgroundColor: CATEGORY_COLORS[notification.category] || '#0097A9' }}
                />
                <div className="notification-item-content">
                  <div className="notification-item-header">
                    <span className="notification-item-icon">
                      {CATEGORY_ICONS[notification.category] || '📌'}
                    </span>
                    <span className="notification-item-title">{notification.title}</span>
                    {notification._source === 'report' && (
                      <Download size={13} style={{ marginLeft: 'auto', color: '#7C3AED', flexShrink: 0 }} />
                    )}
                  </div>
                  <p className="notification-item-message">{notification.message}</p>
                  <div className="notification-item-footer">
                    <span className="notification-item-time">
                      {getRelativeTime(notification.created_at)}
                    </span>
                    {notification.triggered_by_name && (
                      <span className="notification-item-actor">
                        by {notification.triggered_by_name}
                      </span>
                    )}
                  </div>
                </div>
                {!notification.is_read && (
                  <button
                    className="notification-item-read-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      markAsRead(notification.id);
                    }}
                    title="Mark as read"
                  >
                    <Check size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="notification-panel-footer">
              <button
                className="notification-view-all-btn"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/test-calendar');
                }}
              >
                <Calendar size={14} />
                View Test Calendar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
